import React, { useState, useEffect } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Receipt, FileText } from 'lucide-react';
import Sidebar from './Sidebar';
import './Dashboard.css';


const T = {
    navBg:        '#dc3545',
    navAccent:    '#2d5016',
    pageBg:       '#ffffff',
    cardBg:       '#ffffff',
    rowOdd:       '#ffffff',
    rowEven:      '#f8f9fa',
    rowHover:     '#fff5f5',
    headerText:   '#6c757d',
    bodyText:     '#212529',
    mutedText:    '#6c757d',
    debitRed:     '#dc3545',
    borderLight:  '#e9ecef',
    pillBg:       'rgba(15,15,15,0.90)',
};

const TransactionLedger = ({ transactions: propTransactions, loading: propLoading, error: propError }) => {
    const navigate = useNavigate();

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState(null);

    useEffect(() => {
        if (propTransactions !== undefined) {
            setTransactions(propTransactions);
            setLoading(propLoading ?? false);
            setError(propError ?? null);
            return;
        }
        fetchTransactions();
    }, [propTransactions]);

    const fetchTransactions = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('x-cc-auth');
            const sessionId = localStorage.getItem('JSESSIONID');

            const res = await fetch('https://api.chefcoco.com/app/admin/all/transactions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept':       '*/*',
                    'X-CC-AUTH':    token,
                    ...(sessionId ? { 'Cookie': `JSESSIONID=${sessionId}` } : {}),
                },
                body: '',
            });
            if (!res.ok) throw new Error(`Failed to fetch transactions (${res.status})`);
            const data = await res.json();
            setTransactions(data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fmtAmount = (amount) => {
        const abs = Math.abs(amount).toFixed(2);
        return amount < 0 ? `-€${abs}` : `€${abs}`;
    };

    const handleLogout = () => {
        localStorage.removeItem('x-cc-auth');
        window.location.href = '/';
    };

    const userEmail = localStorage.getItem('email') || '';
    const avatarInitials = userEmail ? userEmail.split('@')[0].slice(0, 2).toUpperCase() : '??';
    const multiCompany = new Set(transactions.map(t => t.companyName).filter(Boolean)).size > 1;

    return (
        <div className="dashboard-container">
            <Sidebar
                currentPath="/transactions"
                onNavigate={(path) => navigate(path)}
                onLogout={handleLogout}
            />

            <div className="main-content">
                {/* Topbar */}
                <div className="topbar">
                    <div className="topbar-title">
                        <h2>Invoices</h2>
                        <span className="topbar-badge">Finance · Transactions</span>
                    </div>
                    <button className="avatar-btn" title={userEmail}>{avatarInitials}</button>
                </div>

                <div className="content-area">
                    <div className="section-card" style={{ padding: 0, overflow: 'hidden' }}>

                        {error ? (
                            <Alert variant="danger" className="m-4">{error}</Alert>

                        ) : loading ? (
                            <div style={{ textAlign: 'center', padding: '60px' }}>
                                <Spinner animation="border" style={{ color: '#1a3a28' }} />
                                <p style={{ color: T.mutedText, marginTop: '12px', fontSize: '14px' }}>
                                    Loading transactions...
                                </p>
                            </div>

                        ) : transactions.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px', color: T.mutedText, fontSize: '14px' }}>
                                No transactions found.
                            </div>

                        ) : (
                            <>
                                {/* ── Header ── */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 160px 120px 100px',
                                    padding: '12px 24px',
                                    borderBottom: `2px solid ${T.borderLight}`,
                                }}>
                                    {['DESCRIPTION', 'DATE', 'INVOICE', 'AMOUNT'].map(col => (
                                        <span key={col} style={{
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            color: T.headerText,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.06em',
                                            textAlign: col === 'AMOUNT' ? 'right' : 'left',
                                        }}>
                                            {col}
                                        </span>
                                    ))}
                                </div>

                                {/* ── Rows ── */}
                                {transactions.map((tx, index) => (
                                    <TransactionRow
                                        key={`${tx.companyName || ''}-${tx.id}`}
                                        tx={tx}
                                        showCompany={multiCompany}
                                        isEven={index % 2 !== 0}
                                        fmtAmount={fmtAmount}
                                    />
                                ))}

                                {/* ── Footer pill ── */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '14px 24px',
                                    borderTop: `1px solid ${T.borderLight}`,
                                    background: '#f9fafb',
                                }}>
                                    <PillStat value={transactions.length} label="total" color="#111" />
                                    <PillDivider />
                                    <PillStat value={transactions.filter(t => t.amount >= 0).length} label="credits" color="#16a34a" />
                                    <PillDivider />
                                    <PillStat value={transactions.filter(t => t.amount < 0).length} label="debits" color="#dc2626" />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};


const TransactionRow = ({ tx, isEven, fmtAmount, showCompany }) => {
    const isCredit = tx.amount >= 0;
    const [hovered, setHovered] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const creditMatch = tx.description?.match(/^(Adding credit for .+?)\((.+?)\)$/);
    const mainLabel   = creditMatch ? creditMatch[1] : tx.description;
    const dateTag     = creditMatch ? creditMatch[2] : null;

    const invoiceId = tx.invoiceId || tx.invoice_id || null;
    const isInvoice = !!invoiceId;

    const handleOpenInvoice = async (e) => {
        e.stopPropagation();
        if (downloading) return;

        try {
            setDownloading(true);

            const token = localStorage.getItem('x-cc-auth');
            const sessionId = localStorage.getItem('JSESSIONID');

            const res = await fetch(
                `https://api.chefcoco.com/app/admin/downloadPdf?invoiceId=${encodeURIComponent(invoiceId)}`,
                {
                    method: 'GET',
                    headers: {
                        'Accept':       '*/*',
                        'X-CC-AUTH':    token,
                        'client-type':  'WEB',
                        ...(sessionId ? { 'Cookie': `JSESSIONID=${sessionId}` } : {}),
                    },
                }
            );

            if (!res.ok) throw new Error(`Request failed (${res.status})`);

            const data = await res.json();
            window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
        } catch (err) {
            alert(`Could not open invoice: ${err.message}`);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'grid',
                gridTemplateColumns: '1fr 160px 120px 100px',
                padding: '14px 24px',
                background: hovered ? T.rowHover : isEven ? T.rowEven : T.rowOdd,
                borderBottom: `1px solid ${T.borderLight}`,
                transition: 'background 0.12s',
                alignItems: 'start',
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: T.bodyText, lineHeight: 1.4 }}>
                    {mainLabel}
                    {dateTag && <span style={{ color: T.bodyText }}>({dateTag})</span>}
                </span>
                {showCompany && tx.companyName && (
                    <span style={{ fontSize: '12px', color: T.mutedText, fontWeight: 600 }}>{tx.companyName}</span>
                )}
                <span style={{ fontSize: '12px', color: T.mutedText }}> {tx.balance}</span>
            </div>

            <span style={{ fontSize: '13px', color: T.mutedText, paddingTop: '2px' }}>
                {new Date(tx.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>

            <div style={{ paddingTop: '1px' }}>
                {isInvoice ? (
                    <button
                        onClick={handleOpenInvoice}
                        disabled={downloading}
                        title={`Download invoice ${invoiceId}`}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            background: downloading ? '#f1f3f5' : '#fff5f5',
                            border: `1px solid ${downloading ? '#dee2e6' : '#f5c2c7'}`,
                            borderRadius: '5px',
                            padding: '3px 10px',
                            cursor: downloading ? 'not-allowed' : 'pointer',
                            color: downloading ? T.mutedText : T.debitRed,
                            fontSize: '12px',
                            fontWeight: 500,
                            transition: 'all 0.15s',
                            whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => {
                            if (!downloading) {
                                e.currentTarget.style.background = '#fce8ea';
                                e.currentTarget.style.borderColor = '#dc3545';
                            }
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.background = downloading ? '#f1f3f5' : '#fff5f5';
                            e.currentTarget.style.borderColor = downloading ? '#dee2e6' : '#f5c2c7';
                        }}
                    >
                        {downloading
                            ? <><Spinner animation="border" size="sm" style={{ width: '10px', height: '10px', borderWidth: '1.5px' }} /> Downloading…</>
                            : <><FileText size={12} strokeWidth={2} /> PDF</>
                        }
                    </button>
                ) : (
                    <span style={{ fontSize: '12px', color: 'transparent' }}>—</span>
                )}
            </div>

            <span style={{
                fontSize: '14px',
                fontWeight: 600,
                color: isCredit ? T.bodyText : T.debitRed,
                textAlign: 'right',
                paddingTop: '2px',
            }}>
                {fmtAmount(tx.amount)}
            </span>
        </div>
    );
};


const PillStat = ({ value, label, color = 'rgba(255,255,255,0.9)' }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', padding: '2px 6px' }}>
        <span style={{ color, fontSize: '14px', fontWeight: 700 }}>{value}</span>
        <span style={{ color: 'rgba(255,255,255,0.38)', fontSize: '11px' }}>{label}</span>
    </div>
);

const PillDivider = () => (
    <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.12)', margin: '0 4px' }} />
);

export default TransactionLedger;
